import { useEffect, useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { DEFAULT_COUNTRY_CODE } from "@unreserved/constants";
import { useDebounce } from "@unreserved/hooks/use-debounce";
import {
  type PublicInspectionFormData,
  publicInspectionSchema,
} from "@unreserved/schemas/enquiries/public-inspection-schema";
import type { PropertyListItemDto } from "@unreserved/sdk";
import { Label } from "@unreserved/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@unreserved/ui/components/select";
import { auStateTimezoneUtils, getPropertyTodayPickerDate } from "@unreserved/utils";
import { Controller, useForm } from "react-hook-form";

import { useEnquiryModalDropdown } from "@/app/_components/my-enquiries/modal/enquiry-modal-dropdown-context";
import { PropertyAddressInput } from "@/app/_components/my-enquiries/modal/property-address-input";
import { Input } from "@/app/_components/shared/input";
import { PhoneInput } from "@/app/_components/shared/phone-input";
import {
  buildDatesAndSlots,
  dateKey,
  getPropertyListingState,
  parseDateKey,
  safeDate,
  syncToFirst,
} from "@/app/_libs/utils/my-enquiries/book-inspection-utils";
import { useCreateInspection, usePropertiesForEnquiry } from "@/hooks/my-enquiry/use-my-enquiry";
import { useAuthStore } from "@/stores/auth-store";

const INPUT_STYLES =
  "border-border-400 text-typography-900 placeholder:text-typography-400 focus-visible:border-primary-500 focus-visible:ring-primary-200 h-9 w-full rounded border px-3 text-sm focus-visible:ring-4 focus-visible:outline-none";
const SLOT_BTN_BASE =
  "inline-flex cursor-pointer min-w-0 items-center justify-center rounded border border-border-400 px-2 py-2 sm:min-w-[100px] sm:flex-1 sm:px-4 sm:py-2 text-sm transition-colors";
const SLOT_BTN_SELECTED = "border-primary-500 bg-primary-50 text-primary-700";

export const BookInspectionForm = () => {
  const user = useAuthStore((s) => s.user);
  const {
    setPropertyDropdownOpen,
    setDateTimeVisible,
    preselectedListingId,
    preselectedListingState,
  } = useEnquiryModalDropdown();
  const userEmail = user?.email != null ? String(user.email) : "";
  const userName = user?.fullName != null ? String(user.fullName) : "";
  const userPhoneNumber = user?.phoneNumber != null ? String(user.phoneNumber) : "";
  const [selectedProperty, setSelectedProperty] = useState<PropertyListItemDto | null>(null);
  const timeZone = auStateTimezoneUtils.getTimezoneForState(
    preselectedListingState ?? getPropertyListingState(selectedProperty),
  );
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [propertySearchText, setPropertySearchText] = useState("");
  const debouncedSearch = useDebounce(propertySearchText, 300);
  const [isPropertyDropdownOpen, setIsPropertyDropdownOpen] = useState(false);

  const createInspection = useCreateInspection();
  const { setIsSubmitting, setSuccessConfig } = useEnquiryModalDropdown();
  const {
    data: propertiesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = usePropertiesForEnquiry({
    hasOpenInspections: "yes",
    propertyTitle: debouncedSearch || undefined,
  });

  useEffect(() => {
    setIsSubmitting(createInspection.isPending);
  }, [createInspection.isPending, setIsSubmitting]);

  const properties = useMemo(
    () => propertiesData?.pages?.flatMap((page) => page.data?.properties ?? []) ?? [],
    [propertiesData],
  );

  const propertySuggestions = useMemo(
    () =>
      properties.map((p) => ({
        id: p.id,
        displayLabel: typeof p.title === "string" ? p.title : p.id,
        data: p as PropertyListItemDto,
      })),
    [properties],
  );

  const { dateOptions, slotsByDate } = useMemo(
    () => buildDatesAndSlots(selectedProperty?.openInspections ?? [], timeZone),
    [selectedProperty?.openInspections, timeZone],
  );

  const {
    register: registerField,
    handleSubmit,
    control,
    setValue,
    watch,
    formState,
  } = useForm<PublicInspectionFormData>({
    resolver: zodResolver(publicInspectionSchema),
    defaultValues: {
      email: "",
      name: "",
      countryCode: DEFAULT_COUNTRY_CODE,
      phoneNumber: "",
      propertyAddress: "",
      selectedDate: getPropertyTodayPickerDate(timeZone),
      selectedTime: "",
    },
    mode: "onChange",
  });

  const watchedDate = watch("selectedDate");
  const currentKey = dateKey(watchedDate, timeZone);
  const currentSlots = currentKey ? (slotsByDate.get(currentKey) ?? []) : [];
  const countryCode = watch("countryCode");

  useEffect(() => {
    if (userEmail) setValue("email", userEmail);
    if (userName) setValue("name", userName);
  }, [userEmail, userName, setValue]);

  useEffect(() => {
    if (dateOptions.length === 0) return;
    const key = dateKey(watchedDate, timeZone);
    if (key && dateOptions.some((o) => o.value === key)) return;
    syncToFirst(dateOptions, slotsByDate, setValue, setSelectedSlot);
  }, [dateOptions, watchedDate, slotsByDate, setValue, timeZone]);

  useEffect(() => {
    if (selectedProperty === null) {
      setSelectedSlot(null);
      setValue("selectedDate", getPropertyTodayPickerDate(timeZone));
      setValue("selectedTime", "");
    }
  }, [selectedProperty, setValue, timeZone]);

  useEffect(() => {
    setDateTimeVisible(selectedProperty !== null);
  }, [selectedProperty, setDateTimeVisible]);

  useEffect(() => {
    if (preselectedListingId && properties.length > 0 && !selectedProperty) {
      const match = properties.find(
        (p) => p.id === preselectedListingId || String(p.listingId) === preselectedListingId,
      );
      if (match) {
        setSelectedProperty(match);
        setValue("propertyAddress", typeof match.title === "string" ? match.title : match.id);
      }
    }
  }, [preselectedListingId, properties, selectedProperty, setValue]);

  const selectValue =
    dateOptions.length === 0
      ? ""
      : ((currentKey && dateOptions.some((o) => o.value === currentKey)
          ? currentKey
          : dateOptions[0]?.value) ?? "");

  const showDateError =
    formState.errors.selectedDate &&
    dateOptions.length > 0 &&
    formState.errors.selectedDate.message !== "Invalid date";

  const onSubmit = (data: PublicInspectionFormData) => {
    if (!selectedProperty || !selectedSlot) return;
    const listingId =
      typeof selectedProperty.listingId === "string"
        ? selectedProperty.listingId
        : String(selectedProperty.listingId ?? "");
    if (!listingId) return;

    const slot = selectedProperty.openInspections?.find((s) => s.id === selectedSlot);
    const startDate = slot ? safeDate(slot.inspectionStartTime) : null;
    const endDate = slot ? safeDate(slot.inspectionEndTime) : startDate;
    const inspectionStartTime = startDate ? startDate.toISOString() : undefined;
    const inspectionEndTime = endDate ? endDate.toISOString() : inspectionStartTime;

    createInspection.mutate(
      {
        email: data.email,
        name: data.name,
        countryCode: data.phoneNumber ? data.countryCode : undefined,
        phoneNumber: data.phoneNumber || undefined,
        listingId,
        inspectionType: "open",
        listingInspectionId: selectedSlot,
        inspectionStartTime,
        inspectionEndTime,
      },
      {
        onSuccess: () => {
          setSuccessConfig({
            title: "Public Inspection Booking Successful",
            subheading:
              "For inspection, please be on time and maintain professionalism at property visits",
            primaryButtonRedirect: "dashboard",
          });
        },
      },
    );
  };

  return (
    <form id="book-inspection-form" onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div className="space-y-5">
        <div className="space-y-1.5">
          <Label className="text-typography-750 text-sm font-medium">Email ID</Label>
          <Input
            type="email"
            placeholder="Enter here"
            className={`${INPUT_STYLES} disabled:cursor-not-allowed disabled:opacity-70`}
            {...registerField("email")}
            aria-invalid={!!formState.errors.email}
            disabled={!!userEmail}
          />
          {formState.errors.email && (
            <p className="text-error-600 text-sm">{formState.errors.email.message}</p>
          )}
        </div>

        {!userName && (
          <div className="space-y-1.5">
            <Label className="text-typography-750 text-sm font-medium">Full Name</Label>
            <Input
              type="text"
              placeholder="Enter here"
              className={`${INPUT_STYLES} disabled:cursor-not-allowed disabled:opacity-70`}
              {...registerField("name")}
              aria-invalid={!!formState.errors.name}
            />
            {formState.errors.name && (
              <p className="text-error-600 text-sm">{formState.errors.name.message}</p>
            )}
          </div>
        )}

        {!userPhoneNumber && (
          <Controller
            control={control}
            name="phoneNumber"
            render={({ field }) => (
              <PhoneInput
                label="Phone Number"
                value={field.value}
                onChange={field.onChange}
                countryCode={countryCode}
                onCountryCodeChange={(code) => setValue("countryCode", code)}
                placeholder="Enter phone number"
                error={formState.errors.phoneNumber?.message}
                maxLength={15}
              />
            )}
          />
        )}

        <div className={`space-y-1.5 ${isPropertyDropdownOpen ? "mb-40" : ""}`}>
          <Label className="text-typography-750 text-sm font-medium">Select Property</Label>
          <Controller
            control={control}
            name="propertyAddress"
            render={({ field }) => {
              const selectedDisplayLabel =
                selectedProperty != null
                  ? typeof selectedProperty.title === "string"
                    ? selectedProperty.title
                    : selectedProperty.id
                  : "";
              return (
                <PropertyAddressInput<PropertyListItemDto>
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                    setPropertySearchText(value);
                    if (value === "" || value !== selectedDisplayLabel) {
                      setSelectedProperty(null);
                    }
                  }}
                  placeholder="Search or select property"
                  externalSuggestions={propertySuggestions}
                  onDropdownOpenChange={(open, count) => {
                    setPropertyDropdownOpen(open, count);
                    setIsPropertyDropdownOpen(open);
                  }}
                  scrollToBottomWhenEmpty={true}
                  onSelectSuggestion={(item) => {
                    setSelectedProperty(item.data as PropertyListItemDto);
                    field.onChange(item.displayLabel);
                    setPropertySearchText("");
                  }}
                  onEndReached={() => {
                    if (hasNextPage && !isFetchingNextPage) {
                      void fetchNextPage();
                    }
                  }}
                  loadingMore={isFetchingNextPage}
                  hasMore={hasNextPage}
                  loading={isLoading}
                />
              );
            }}
          />
          {(formState.touchedFields.propertyAddress || formState.submitCount > 0) &&
            formState.errors.propertyAddress && (
              <p className="text-error-600 text-sm">{formState.errors.propertyAddress.message}</p>
            )}
        </div>

        {selectedProperty !== null && (
          <>
            <div className="space-y-1.5">
              <Label className="text-typography-750 text-sm font-medium">Select Date</Label>
              <Controller
                control={control}
                rules={{ required: "Select date is required" }}
                name="selectedDate"
                render={({ field }) => (
                  <Select
                    value={selectValue}
                    onValueChange={(value) => {
                      if (!value) return;
                      const parsed = parseDateKey(value);
                      if (!parsed) return;
                      field.onChange(parsed);
                      const slots = slotsByDate.get(value) ?? [];
                      const firstId = slots[0]?.id ?? "";
                      setValue("selectedTime", firstId);
                      setSelectedSlot(firstId || null);
                    }}
                    disabled={dateOptions.length === 0}>
                    <SelectTrigger
                      className={`${INPUT_STYLES} focus-visible:ring-primary-200 bg-white! px-3`}>
                      <SelectValue
                        placeholder={
                          dateOptions.length === 0 ? "Select a property first" : "Select"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent side="bottom" className="cursor-pointer bg-white! text-black">
                      {dateOptions.map((opt) => (
                        <SelectItem
                          key={opt.value}
                          value={opt.value}
                          className="bg-white! text-black focus:bg-white! focus:text-black">
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {showDateError && (
                <p className="text-error-600 text-sm">{formState.errors.selectedDate?.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <input type="hidden" {...registerField("selectedTime")} />
              <Label className="text-typography-750 block text-sm font-medium">
                Select Time Slot
              </Label>
              <div className="grid grid-cols-2 gap-3 sm:flex sm:grid-cols-3 sm:flex-wrap">
                {currentSlots.map((slot) => (
                  <button
                    key={slot.id}
                    type="button"
                    onClick={() => {
                      setSelectedSlot(slot.id);
                      setValue("selectedTime", slot.id);
                    }}
                    className={`${SLOT_BTN_BASE} ${selectedSlot === slot.id ? SLOT_BTN_SELECTED : ""}`}>
                    {slot.label}
                  </button>
                ))}
              </div>
              {currentSlots.length === 0 && (
                <p className="text-typography-500 text-sm">
                  {selectedProperty
                    ? "No time slots for this date."
                    : "Select a property to see available dates and times."}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </form>
  );
};
